import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft, ShieldCheck, Smartphone, CheckCircle2, Download, Copy, Info, AlertCircle, QrCode } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";

/* ═══════════════════════════════════════════════════════════════════
   Mobile-only Static UPI Payment System
   Route  /static-qr                    → Amount entry
          /static-qr/checkout           → Customer form + Pay button
          /static-qr/pay/:paymentId     → UPI app launcher + UTR capture
          /static-qr/receipt/:paymentId → Success card + PDF download
   ═══════════════════════════════════════════════════════════════════ */

const useIsMobile = () => {
  const [mobile, setMobile] = useState(true);
  useEffect(() => {
    const check = () => {
      const w = typeof window !== "undefined" ? window.innerWidth : 0;
      const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
      const isMob = /android|iphone|ipod|windows phone|iemobile|opera mini|mobile/i.test(ua) || w <= 900;
      setMobile(isMob);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return mobile;
};

function DesktopBlock() {
  // Same URL the visitor is currently on — so scanning the QR opens the
  // identical page on their phone with any query params preserved.
  const currentUrl = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}${window.location.search}`
    : "https://optivize.digital/static-qr";
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 flex items-center justify-center p-6" data-testid="upi-desktop-block">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-8">
        <div className="flex items-center gap-3 pb-4 mb-4 border-b border-slate-100">
          <img src="/optivize-logo-mark.png" alt="Optivize" className="h-9 w-auto" onError={(e)=>{e.currentTarget.style.display='none';}}/>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900 leading-none" style={{fontFamily:"Outfit"}}>Optivize</div>
            <div className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase mt-0.5">
              A Unit of <span className="text-blue-700">SURAJIT MAJHI HUF</span>
            </div>
          </div>
        </div>
        <div className="text-center">
          <Smartphone size={40} className="text-blue-600 mx-auto"/>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mt-3" style={{fontFamily:"Outfit"}}>
            This payment page is available only on mobile devices.
          </h1>
          <p className="text-sm text-slate-500 mt-2">Scan the QR code with your phone to continue.</p>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
          {/* QR code — opens the exact same URL on the phone */}
          <div className="shrink-0 rounded-2xl bg-gradient-to-br from-blue-50 via-white to-orange-50 border-2 border-blue-100 p-3 shadow-inner" data-testid="upi-desktop-qr">
            <QRCodeSVG
              value={currentUrl}
              size={168}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#0f172a"
              imageSettings={{
                src: "/optivize-logo-mark.png",
                height: 28, width: 28,
                excavate: true,
              }}
            />
            <div className="text-center text-[10px] font-bold tracking-widest uppercase text-blue-700 mt-2 inline-flex items-center gap-1 justify-center w-full">
              <QrCode size={11}/> Scan with phone
            </div>
          </div>

          <div className="flex-1 min-w-0 text-left">
            <div className="text-[11px] uppercase tracking-widest text-blue-700 font-semibold">How to scan</div>
            <ol className="text-xs text-slate-600 mt-2 space-y-1.5 list-decimal list-inside">
              <li>Open your phone&apos;s <strong>Camera</strong> app.</li>
              <li>Point it at the QR code on the left.</li>
              <li>Tap the notification banner to open the link.</li>
            </ol>
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Or copy the link</div>
              <div className="mt-1 flex items-center gap-1.5">
                <input readOnly value={currentUrl}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded bg-white border border-slate-200 text-[11px] font-mono text-slate-700"
                  data-testid="upi-desktop-url"/>
                <button
                  type="button"
                  onClick={async ()=>{ try{ await navigator.clipboard.writeText(currentUrl); toast.success("Link copied"); }catch{ toast.error("Could not copy"); } }}
                  className="px-2 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold inline-flex items-center gap-1"
                  data-testid="upi-desktop-copy">
                  <Copy size={10}/> Copy
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl bg-blue-50 border border-blue-100 p-3 text-left">
          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Why mobile only?</div>
          <p className="text-xs text-slate-600 mt-1">UPI apps (Google Pay, PhonePe, Paytm, BHIM) live on your phone. We use a <code>upi://pay</code> deep link that only works on Android and iOS.</p>
        </div>
      </div>
    </div>
  );
}

function OptivizeHeader() {
  return (
    <div className="flex items-center gap-3 pb-4 mb-4 border-b border-slate-100">
      <img src="/optivize-logo-mark.png" alt="Optivize" className="h-9 w-auto" onError={(e)=>{e.currentTarget.style.display='none';}}/>
      <div className="min-w-0">
        <div className="text-sm font-bold text-slate-900 leading-none" style={{fontFamily:"Outfit"}}>Optivize</div>
        <div className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase mt-0.5">
          A Unit of <span className="text-blue-700">SURAJIT MAJHI HUF</span>
        </div>
      </div>
    </div>
  );
}

function TrustBar() {
  return (
    <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-3 justify-center">
      <ShieldCheck size={12} className="text-emerald-600"/>
      <span>Secure UPI payment · Direct to bank</span>
    </div>
  );
}

/* ─────────────────  Step 1 · Amount  ───────────────── */
export function StaticUpiAmount() {
  const mobile = useIsMobile();
  const nav = useNavigate();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  if (!mobile) return <DesktopBlock/>;

  const go = async (e) => {
    e.preventDefault();
    const a = parseFloat(amount);
    if (!(a > 0)) { toast.error("Please enter a valid amount"); return; }
    setLoading(true);
    try {
      // Pre-flight: make sure the amount is fulfillable so we don't send the
      // customer to a checkout that would 503 on submit.
      await api.post("/static-upi/quote", { amount: a });
      nav("/static-qr/checkout", { state: { amount: a } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No UPI ID available for this amount right now");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 py-6 px-4" data-testid="upi-amount-page">
      <div className="max-w-sm mx-auto">
        <div className="bg-white rounded-2xl shadow-md ring-1 ring-slate-200 p-5">
          <OptivizeHeader/>
          <div className="text-center">
            <div className="text-[11px] tracking-[0.22em] text-orange-600 font-bold uppercase">Static UPI Payment</div>
            <h1 className="text-2xl font-black text-slate-900 mt-1" style={{fontFamily:"Outfit"}}>How much would you like to pay?</h1>
            <p className="text-xs text-slate-500 mt-1.5">Direct UPI transfer to the Optivize business account.</p>
          </div>

          <form onSubmit={go} className="mt-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Amount (INR) *</label>
              <div className="flex items-center rounded-xl border-2 border-slate-200 focus-within:border-blue-500 bg-slate-50 focus-within:bg-white transition">
                <span className="pl-4 text-slate-500 text-lg font-semibold">₹</span>
                <input
                  type="number" inputMode="decimal" step="1" min="1"
                  value={amount} onChange={(e)=>setAmount(e.target.value)}
                  placeholder="0"
                  className="flex-1 bg-transparent py-3.5 px-3 text-2xl font-bold text-slate-900 outline-none"
                  data-testid="upi-amount-input" required autoFocus
                />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-lg shadow-blue-600/30 active:scale-[0.99] transition"
              data-testid="upi-amount-go">
              {loading ? <Loader2 size={18} className="animate-spin"/> : "Go"}
              {!loading && <ArrowRight size={18}/>}
            </button>
          </form>
          <TrustBar/>
        </div>
        <PoweredBy/>
      </div>
    </div>
  );
}

/* ─────────────────  Step 2 · Customer details  ───────────────── */
export function StaticUpiCheckout() {
  const mobile = useIsMobile();
  const nav = useNavigate();
  const { state } = useLocation();
  const amount = state?.amount;
  const [form, setForm] = useState({ name:"", mobile:"", email:"", address:"", city:"", state:"", postal_code:"" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (!amount) nav("/static-qr", { replace: true }); }, [amount, nav]);
  if (!mobile) return <DesktopBlock/>;

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/static-upi/payments", { amount, ...form });
      // Move to the UPI launcher with the created payment id
      nav(`/static-qr/pay/${data.id}`, { state: { payment: data } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 py-6 px-4" data-testid="upi-checkout-page">
      <div className="max-w-sm mx-auto">
        <button onClick={()=>nav(-1)} className="inline-flex items-center gap-1.5 text-slate-600 text-xs mb-3">
          <ArrowLeft size={12}/> Change amount
        </button>
        <div className="bg-white rounded-2xl shadow-md ring-1 ring-slate-200 p-5">
          <OptivizeHeader/>
          <div className="mb-4 flex items-center justify-between rounded-xl bg-blue-50 border border-blue-100 px-3 py-2.5">
            <span className="text-[11px] uppercase tracking-widest text-blue-700 font-semibold">Amount to pay</span>
            <span className="text-lg font-black text-blue-800" style={{fontFamily:"Outfit"}} data-testid="upi-checkout-amount">₹{Number(amount || 0).toLocaleString("en-IN")}</span>
          </div>

          <form onSubmit={pay} className="space-y-3">
            <Field label="Name *" v={form.name} on={set("name")} testid="upi-name" ac="name"/>
            <Field label="Mobile *" v={form.mobile} on={set("mobile")} testid="upi-mobile" type="tel" ac="tel"/>
            <Field label="Email *" v={form.email} on={set("email")} testid="upi-email" type="email" ac="email"/>
            <Field label="Address *" v={form.address} on={set("address")} testid="upi-address" ac="street-address"/>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="City *" v={form.city} on={set("city")} testid="upi-city" ac="address-level2"/>
              <Field label="State *" v={form.state} on={set("state")} testid="upi-state" ac="address-level1"/>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Postal Code *" v={form.postal_code} on={set("postal_code")} testid="upi-postal" ac="postal-code" inputMode="numeric"/>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Country</label>
                <input value="India" readOnly className="w-full py-2.5 px-3 rounded-lg bg-slate-100 text-slate-500 text-sm cursor-not-allowed" data-testid="upi-country"/>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 mt-4 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-lg shadow-blue-600/30 active:scale-[0.99] transition"
              data-testid="upi-pay-btn">
              {loading ? <Loader2 size={18} className="animate-spin"/> : "Pay"}
              {!loading && <ArrowRight size={18}/>}
            </button>
          </form>
          <TrustBar/>
        </div>
        <PoweredBy/>
      </div>
    </div>
  );
}

function Field({ label, v, on, testid, type="text", ac, inputMode }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</label>
      <input value={v} onChange={on} type={type} required autoComplete={ac} inputMode={inputMode}
        className="w-full py-2.5 px-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm text-slate-900 bg-white"
        data-testid={testid}/>
    </div>
  );
}

/* ─────────────────  Step 3a · UPI launcher + UTR capture  ───────────────── */
export function StaticUpiPay() {
  const mobile = useIsMobile();
  const { paymentId } = useParams();
  const nav = useNavigate();
  const { state } = useLocation();
  const [payment, setPayment] = useState(state?.payment || null);
  const [loading, setLoading] = useState(!state?.payment);
  const [utr, setUtr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [noUpi, setNoUpi] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (payment) return;
    (async () => {
      try {
        const { data } = await api.get(`/static-upi/payments/${paymentId}`);
        if (!cancelled) setPayment(data);
      } catch { toast.error("Payment not found"); nav("/static-qr", { replace: true }); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [paymentId, payment, nav]);

  // Auto-launch UPI intent on mount (once we have the payment record)
  useEffect(() => {
    if (!payment?.upi_link) return;
    // A brief delay lets the "Redirecting…" splash render before the
    // OS chooser sheet slides up.
    const t = setTimeout(() => {
      const before = Date.now();
      window.location.href = payment.upi_link;
      // If nothing intercepts within 3s, no UPI app is installed.
      setTimeout(() => {
        if (Date.now() - before > 2500 && document.visibilityState === "visible") {
          setNoUpi(true);
        }
      }, 3000);
    }, 300);
    return () => clearTimeout(t);
  }, [payment?.upi_link]);

  if (!mobile) return <DesktopBlock/>;
  if (loading || !payment) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-500" size={38}/></div>;
  }

  const submitUtr = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/static-upi/payments/${paymentId}/utr`, { utr: utr.trim() });
      toast.success("Payment confirmed — receipt ready!");
      nav(`/static-qr/receipt/${paymentId}`, { state: { payment: data } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm UTR");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 py-6 px-4" data-testid="upi-pay-page">
      <div className="max-w-sm mx-auto">
        <div className="bg-white rounded-2xl shadow-md ring-1 ring-slate-200 p-5">
          <OptivizeHeader/>

          {/* Redirect splash */}
          {!noUpi && (
            <div className="text-center py-3">
              <div className="w-14 h-14 rounded-full bg-blue-100 mx-auto flex items-center justify-center animate-pulse">
                <Smartphone size={26} className="text-blue-600"/>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mt-3" style={{fontFamily:"Outfit"}}>Opening your UPI app…</h2>
              <p className="text-xs text-slate-500 mt-1">Choose Google Pay, PhonePe, Paytm, BHIM, Amazon Pay or any other installed UPI app.</p>
              <a href={payment.upi_link}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold text-sm active:scale-[0.99]"
                data-testid="upi-relaunch-btn">
                Open UPI app again <ArrowRight size={12}/>
              </a>
            </div>
          )}
          {noUpi && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
              <AlertCircle size={22} className="text-amber-600 mx-auto"/>
              <div className="text-sm font-bold text-slate-900 mt-2">No UPI app found</div>
              <p className="text-xs text-slate-600 mt-1">Please install a UPI app (Google Pay, PhonePe, Paytm or BHIM) and try again.</p>
              <a href={payment.upi_link}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold text-xs"
                data-testid="upi-retry-btn">
                Retry launch <ArrowRight size={11}/>
              </a>
            </div>
          )}

          {/* Payment summary */}
          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-xs" data-testid="upi-pay-summary">
            <Row k="Payee" v={payment.upi_holder_name || "SURAJIT MAJHI HUF"}/>
            <Row k="UPI ID" v={payment.upi_id} copy/>
            <Row k="Amount" v={`₹${Number(payment.amount).toLocaleString("en-IN")}`}/>
            <Row k="Receipt #" v={payment.receipt_number}/>
          </div>

          {/* UTR capture */}
          <form onSubmit={submitUtr} className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold">
              <CheckCircle2 size={14}/> Already paid? Enter your UTR / transaction ID to confirm
            </div>
            <input
              value={utr} onChange={(e)=>setUtr(e.target.value)}
              placeholder="e.g. 4128-0921-4732 or bank ref number"
              className="w-full py-3 px-3 rounded-lg border border-emerald-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm bg-white"
              data-testid="upi-utr-input" required minLength={6}
            />
            <p className="text-[10px] text-slate-600">You&apos;ll find the UTR in your UPI app&apos;s transaction history — usually a 12-digit number.</p>
            <button type="submit" disabled={submitting || !utr.trim()}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm active:scale-[0.99]"
              data-testid="upi-utr-submit">
              {submitting ? <Loader2 size={16} className="animate-spin"/> : "Confirm payment"}
              {!submitting && <CheckCircle2 size={14}/>}
            </button>
          </form>
          <TrustBar/>
        </div>
        <PoweredBy/>
      </div>
    </div>
  );
}

function Row({ k, v, copy = false }) {
  const doCopy = async () => { try { await navigator.clipboard.writeText(v); toast.success("Copied"); } catch { /* noop */ } };
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-900 font-mono font-semibold inline-flex items-center gap-1.5">
        {v}
        {copy && <button type="button" onClick={doCopy} className="text-slate-400 hover:text-blue-600"><Copy size={11}/></button>}
      </span>
    </div>
  );
}

/* ─────────────────  Step 3b · Receipt / confirmation  ───────────────── */
export function StaticUpiReceipt() {
  const mobile = useIsMobile();
  const { paymentId } = useParams();
  const { state } = useLocation();
  const [payment, setPayment] = useState(state?.payment || null);
  const [loading, setLoading] = useState(!state?.payment);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (payment) return;
    (async () => {
      try {
        const { data } = await api.get(`/static-upi/payments/${paymentId}`);
        if (!cancelled) setPayment(data);
      } catch { toast.error("Payment not found"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [paymentId, payment]);

  // Auto-download the PDF once when the page mounts with a paid record
  useEffect(() => {
    if (!payment || payment.status !== "paid" || downloaded) return;
    const url = `/api/static-upi/payments/${paymentId}/receipt.pdf`;
    const a = document.createElement("a");
    a.href = url; a.download = `${payment.receipt_number || "receipt"}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setDownloaded(true);
  }, [payment, downloaded, paymentId]);

  if (!mobile) return <DesktopBlock/>;
  if (loading || !payment) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-500" size={38}/></div>;

  const isPaid = payment.status === "paid";

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 py-6 px-4" data-testid="upi-receipt-page">
      <div className="max-w-sm mx-auto">
        <div className="bg-white rounded-2xl shadow-md ring-1 ring-slate-200 overflow-hidden" data-testid={`upi-receipt-${payment.status}`}>
          <div className={`h-1.5 ${isPaid ? "bg-gradient-to-r from-orange-500 via-emerald-500 to-blue-600" : "bg-gradient-to-r from-amber-500 to-orange-500"}`}/>
          <div className="p-5">
            <OptivizeHeader/>
            <div className="text-center">
              {isPaid ? <CheckCircle2 size={52} className="text-emerald-500 mx-auto"/> : <Info size={52} className="text-amber-500 mx-auto"/>}
              <h1 className="text-xl font-bold text-slate-900 mt-3" style={{fontFamily:"Outfit"}}>
                {isPaid ? "Payment Received" : "Payment Pending"}
              </h1>
              {isPaid && <p className="text-xs text-slate-500 mt-1">Thank you! We&apos;ve received your UPI payment.</p>}
            </div>
            <div className="mt-5 text-sm space-y-1.5">
              <Row k="Receipt #" v={payment.receipt_number}/>
              <Row k="Customer" v={payment.name}/>
              <Row k="Amount" v={`₹${Number(payment.amount).toLocaleString("en-IN")}`}/>
              <Row k="UPI ID" v={payment.upi_id}/>
              <Row k="UTR / Txn ID" v={payment.utr || "—"}/>
              <Row k="Date &amp; time" v={(payment.paid_at || payment.created_at || "").slice(0,19).replace("T"," ")}/>
              <Row k="Status" v={payment.status.toUpperCase()}/>
            </div>
            {isPaid && (
              <a href={`/api/static-upi/payments/${paymentId}/receipt.pdf`} download
                className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm active:scale-[0.99]"
                data-testid="upi-download-receipt">
                <Download size={14}/> Download PDF receipt
              </a>
            )}
            <Link to="/static-qr" className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold text-xs">
              Make another payment
            </Link>
          </div>
        </div>
        <PoweredBy/>
      </div>
    </div>
  );
}

function PoweredBy() {
  return (
    <p className="text-center text-[10px] text-slate-400 mt-4">
      Powered by <span className="font-semibold text-slate-500">Optivize Digital Payment System</span> ·{" "}
      <a href="https://optivize.digital" className="text-blue-600">optivize.digital</a>
    </p>
  );
}
